import { type BasePagination } from "../../types/api.ts"

/**
 * The three states a paginated list passes through when it is scrolled rather
 * than paged. Every infinite-scroll slice answers a fetch the same way, and the
 * answers are subtle enough that six hand-written copies would be six chances
 * to get one wrong — the project tabs already proved that.
 *
 * A fourth rule these three cannot enforce: only the newest request may write.
 * A search fired mid-scroll answers second, and the load-more it overtook would
 * otherwise append the old filter's page underneath it. Each slice records the
 * pending `requestId` and drops a fulfilled or rejected that no longer carries
 * it — the guard has to suppress the fetching flag too, so it can only live as
 * an early return in the reducer.
 */

/**
 * Rows a scrolled list asks for at a time. `UI_UX_GUIDELINES.md` §"Row-count →
 * rendering strategy" sets the batch at 50; a list that still pages keeps its
 * own size, because that one is also how many rows a page shows.
 */
export const SCROLL_PAGE_SIZE = 50

export interface PagedList<T = unknown> {
  count: number
  next?: string | null
  results: T[]
  /** The page a load-more failed on, kept so it can be offered back. */
  retryNext?: string | null
}

/**
 * A page as it is held in a slice: what the server sent, plus the retry pointer.
 * For the lists that store the response type itself rather than `PagedList` —
 * so `retryNext` reaches them without being added to the wire contract.
 */
export type StoredPage<T, S = undefined> = BasePagination<T, S> &
  Pick<PagedList, "retryNext">

/**
 * The page a load-more should ask for.
 *
 * Two fields answer that, and which one holds the url depends on whether the
 * page before it failed: a failure moves it from `next` to `retryNext` so the
 * scroll listener stops asking on its own and the footer can offer it instead.
 * A caller that reads only `next` therefore has a "Try again" button that does
 * nothing.
 */
export function nextPageUrl(
  page?: Pick<PagedList, "next" | "retryNext"> | null
): string | null {
  return page?.next ?? page?.retryNext ?? null
}

/**
 * On the way out. A load-more leaves the rows alone; anything else — a filter,
 * a search term, a sort — is a different result set, so the pages already
 * accumulated go with it rather than sitting above rows they no longer match.
 */
export function beforeFetch<L extends PagedList>(
  loaded: L,
  initial: L,
  isMore?: boolean
): L {
  return isMore ? loaded : initial
}

/** On success. Only a load-more appends. */
export function afterFetch<L extends PagedList>(
  loaded: L,
  page: L,
  isMore?: boolean
): L {
  if (!isMore) return page

  loaded.count = page.count
  loaded.next = page.next ?? null
  loaded.retryNext = null
  loaded.results.push(...(page.results as L["results"]))
  return loaded
}

/**
 * On failure. A first page falls back to empty; a load-more keeps the pages
 * already on screen and stops offering the one that failed — `hasMore` derives
 * from `next`, and the scroll hook's "content fits" fallback re-fires on every
 * settle while it stands, which against a failing endpoint is an unbounded
 * retry rather than a kindness.
 *
 * Not offering it automatically is not the same as losing it: the url moves to
 * `retryNext`, which is what the list footer offers the user, so the rest of a
 * list is one click away rather than unreachable until the filters are touched.
 */
export function afterFailedFetch<L extends PagedList>(
  loaded: L,
  initial: L,
  isMore?: boolean
): L {
  if (!isMore) return initial

  // `beforeFetch` leaves `next` alone on a load-more, so it still holds the url
  // this request was built from — except on a retry, which is built from
  // `retryNext` precisely because `next` was cleared by the failure before it.
  // Reading `next` alone there would drop the url on the second failure and
  // leave the rest of the list unreachable.
  loaded.retryNext = loaded.next ?? loaded.retryNext ?? null
  loaded.next = null
  return loaded
}
